import React, { Suspense } from 'react';
import { VersionModal } from '@teaminverso/version-modal';
import './assets/css/App.css';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ClerkProvider, SignedIn, SignedOut } from '@clerk/clerk-react';
import { ChakraProvider } from '@chakra-ui/react';
import theme from "theme/theme";
import { ColorModeScript } from "@chakra-ui/react";

// Lazy load components
const AuthLayout = React.lazy(() => import('./layouts/auth'));
const AdminRoute = React.lazy(() => import('./components/routing/AdminRoute'));
const SignUpPage = React.lazy(() => import('./views/auth/signUp'));
const InvitationAccept = React.lazy(() => import('./views/auth/InvitationAccept'));

// Future flags for React Router v7
const routerFutureConfig = {
  v7_startTransition: true,
  v7_relativeSplatPath: true
};

if (!process.env.REACT_APP_CLERK_PUBLISHABLE_KEY) {
  throw new Error("Missing Clerk Publishable Key");
}

// Clerk publishable key
const clerkPubKey = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;

// Loading component
const LoadingFallback = () => (
  <div style={{ 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center', 
    height: '100vh' 
  }}>
    Loading...
  </div>
);

// Debug Environment component
const DebugEnv = () => (
  <div style={{ padding: 20 }}>
    <h2>Environment Variables</h2>
    <pre>
      {JSON.stringify(
        {
          REACT_APP_API_URL: process.env.REACT_APP_API_URL,
          NODE_ENV: process.env.NODE_ENV,
          CLERK_STATUS: !!window.Clerk?.session ? 'Authenticated' : 'Not Authenticated'
        },
        null,
        2
      )}
    </pre>
  </div>
);

// Main routes component that has access to Clerk context
const AppRoutes = () => {
  return (
    <Suspense fallback={<LoadingFallback />}>
      <Routes>
        {/* Public invitation acceptance route */}
        <Route path="/accept-invitation" element={<InvitationAccept />} />
        
        {/* Dedicated signup route for invitation links */}
        <Route path="/sign-up" element={<SignUpPage />} />
        
        {/* Auth routes */}
        <Route path="/auth/*" element={<AuthLayout />} />

        {/* Protected admin routes */}
        <Route path="/admin/*" element={
          <>
            <SignedIn>
              <AdminRoute />
            </SignedIn>
            <SignedOut>
              <Navigate to="/auth/sign-in" replace />
            </SignedOut>
          </>
        } />


        {/* Root path - redirect based on auth state */}
        <Route path="/" element={
          <>
            <SignedIn>
              <Navigate to="/admin" replace />
            </SignedIn>
            <SignedOut>
              <Navigate to="/auth/sign-in" replace />
            </SignedOut>
          </>
        } />

        {/* Catch-all redirect to sign in */}
        <Route path="*" element={<Navigate to="/auth/sign-in" replace />} />
      </Routes>
    </Suspense>
  );
};

function App() {
  if (!clerkPubKey) {
    console.error('Missing Clerk Publishable Key');
    return <div>Configuration Error</div>;
  }

  return (
    <Router future={routerFutureConfig}>
      <ClerkProvider 
        publishableKey={clerkPubKey}
        signInUrl="/auth/sign-in"
        signUpUrl="/sign-up"
        afterSignOutUrl="/auth/sign-in"
        allowedRedirectOrigins={[process.env.REACT_APP_CONVERTIFY_URL]}
        frontendApi={process.env.REACT_APP_CLERK_FRONTEND_API_URL}
        domain={process.env.REACT_APP_CLERK_DOMAIN}
      >
        <ChakraProvider theme={theme}>
          <ColorModeScript initialColorMode={theme.config.initialColorMode} />
          <AppRoutes />
          <VersionModal
            serviceName="Dashboard"
            apiConfig={{
              baseUrl: process.env.REACT_APP_API_URL,
              endpoint: '/v1/version'
            }}
          />
        </ChakraProvider>
      </ClerkProvider>
    </Router>
  );
}

export default App;
